import React, { useState, useEffect, useMemo } from "react";
import { Button, Dropdown } from "antd";
import { SettingOutlined } from "@ant-design/icons";

const ThemeSwitcher = () => {
  // 恢复currentTheme状态变量，用于跟踪当前主题
  const [currentTheme, setCurrentTheme] = useState("wuxia");

  // 使用useMemo缓存themes对象，避免每次渲染都重新创建
  const themes = useMemo(() => ({
    wuxia: {
      name: "武侠水墨",
      background: "/背景图_武侠.png", // 使用绝对路径
      colors: {
        primary: "#495057",
        secondary: "#343a40",
        accent: "#212529",
        text: "#343a40",
        cardBg: "rgba(248, 249, 250, 0.85)",
        cardBorder: "rgba(206, 212, 218, 0.6)"
      }
    },
    cyberpunk: {
      name: "赛博朋克",
      background: "/背景图_赛博朋克.png", // 使用绝对路径
      colors: {
        primary: "#ff00ff",
        secondary: "#00ffff",
        accent: "#ff6b35",
        text: "#00ffff", // 改为闪耀的紫红色
        cardBg: "rgba(0, 0, 0, 0.7)",
        cardBorder: "rgba(255, 0, 255, 0.6)"
      }
    },
    nature: {
      name: "自然风光",
      background: "/背景图_自然.png", // 使用绝对路径
      colors: {
        primary: "#2e8b57",
        secondary: "#228b22",
        accent: "#32cd32",
        text: "#2f4f4f",
        cardBg: "rgba(255, 255, 255, 0.9)",
        cardBorder: "rgba(46, 139, 87, 0.6)"
      }
    },
    // 添加压抑主题
    depressed: {
      name: "泪如雨崩",
      background: "/背景图_雨崩.png", // 请替换为你的压抑背景图片文件名
      colors: {
        primary: "#424242", /* 深灰色 */
        secondary: "#212121", /* 接近黑色 */
        accent: "#616161", /* 中灰色 */
        text: "#B0B0B0", /* 浅灰色文字 */
        cardBg: "rgba(66, 66, 66, 0.7)", /* 深灰色半透明背景 */
        cardBorder: "rgba(97, 97, 97, 0.5)" /* 灰色边框 */
      }
    },
    // 添加萌宠主题
    pet: {
      name: "萌宠乐园",
      background: "/背景图_萌宠.png", // 请替换为你的萌宠背景图片文件名
      colors: {
        primary: "#FF6B9D", /* 粉色 */
        secondary: "#FFB6C1", /* 浅粉色 */
        accent: "#FFD700", /* 金色 */
        text: "#8B4513", /* 棕色文字 */
        cardBg: "rgba(255, 250, 240, 0.9)", /* 浅米色半透明背景 */
        cardBorder: "rgba(255, 182, 193, 0.6)" /* 浅粉色边框 */
      }
    },
    // 添加万家灯火主题
    lights: {
      name: "万家灯火",
      background: "/背景图_万家灯火.png", // 使用绝对路径
      colors: {
        primary: "#FFD700", /* 明亮的黄色，象征希望和光明 */
        secondary: "#FF8C00", /* 温暖的橙色，增强活力感 */
        accent: "#FF4500", /* 红色，代表冲破黑暗的力量 */
        text: "#ff6d00", /* 深棕色文字，确保可读性 */
        cardBg: "rgba(255, 250, 240, 0.9)", /* 半透明米色背景 */
        cardBorder: "rgba(255, 215, 0, 0.6)" /* 金色边框 */
      }
    }
  }), []); // 空依赖数组，表示themes对象只在组件挂载时创建一次

  const applyTheme = (theme) => {
    const root = document.documentElement;
    
    // 设置CSS变量
    root.style.setProperty('--theme-primary', theme.colors.primary);
    root.style.setProperty('--theme-secondary', theme.colors.secondary);
    root.style.setProperty('--theme-accent', theme.colors.accent);
    root.style.setProperty('--theme-text', theme.colors.text);
    root.style.setProperty('--theme-card-bg', theme.colors.cardBg);
    root.style.setProperty('--theme-card-border', theme.colors.cardBorder);
    
    // 设置背景图片 - 使用!important来覆盖CSS中的设置
    document.body.style.backgroundImage = `
      linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.1)),
      url('${theme.background}')
    `;
    
    // 设置data-theme属性
    document.body.setAttribute("data-theme", 
      theme.name === "武侠水墨" ? "wuxia" : 
      theme.name === "赛博朋克" ? "cyberpunk" : 
      theme.name === "自然风光" ? "nature" : 
      theme.name === "泪如雨崩" ? "depressed" : 
      theme.name === "万家灯火" ? "lights" : "pet");
  };

  useEffect(() => {
    // 从localStorage加载保存的主题
    const savedTheme = localStorage.getItem("blog-theme");
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
      applyTheme(themes[savedTheme]);
    } else {
      // 如果没有保存的主题，应用默认主题
      setCurrentTheme("wuxia");
      applyTheme(themes.wuxia);
    }
  }, [themes]); // 只在组件挂载时执行一次

  // 添加一个useEffect来监听currentTheme变化，确保主题正确应用
  useEffect(() => {
    if (themes[currentTheme]) {
      applyTheme(themes[currentTheme]);
    }
  }, [currentTheme, themes]);

  const handleThemeChange = (themeKey) => {
    setCurrentTheme(themeKey);
    localStorage.setItem("blog-theme", themeKey);
  };

  const menuItems = {
    items: [
      {
        key: 'wuxia',
        label: '🎋 武侠水墨',
        onClick: () => handleThemeChange('wuxia')
      },
      {
        key: 'cyberpunk',
        label: '🤖 赛博朋克',
        onClick: () => handleThemeChange('cyberpunk')
      },
      {
        key: 'nature',
        label: '🌿 自然风光',
        onClick: () => handleThemeChange('nature')
      },
      {
        key: 'depressed',
        label: '🌑 泪如雨崩',
        onClick: () => handleThemeChange('depressed')
      },
      {
        key: 'pet',
        label: '🐶 这是我家',
        onClick: () => handleThemeChange('pet')
      },
      {
        key: 'lights',
        label: '🏮 万家灯火',
        onClick: () => handleThemeChange('lights')
      },
    ]
  };

  return (
    <Dropdown 
      menu={menuItems}
      placement="bottomRight"
      trigger={['click']}
    >
      <Button 
        type="text" 
        icon={<SettingOutlined />}
        style={{ 
          color: "rgba(255, 255, 255, 0.9)",
          border: "1px solid rgba(255, 255, 255, 0.3)",
          background: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px)"
        }}
      >
        主题设置
      </Button>
    </Dropdown>
  );
};

export default ThemeSwitcher;